(function () {
  'use strict';

  const CAMPAIGN = 'gold2026';
  const GIFT = '_chrissie_gold_gift';
  const CODE = '_chrissie_gold_code';
  const TX = '_chrissie_gold_transaction';
  const TRIGGER = '_chrissie_gold_trigger';
  const TRIGGER_ID = '59167034835025';
  const PREFIXES = ['GOLD-OMAGGIO-', 'CHRGOLD26-'];
  const ENDS_AT = Date.parse('2027-01-01T00:00:00+01:00');
  const numericId = value => String(value || '').split('/').pop();
  const isGift = line => line.properties && line.properties[GIFT] === CAMPAIGN;
  const isTrigger = line => numericId(line.variant_id || line.id) === TRIGGER_ID && !line.parent_relationship;
  const linesFor = (cart, id) => (cart.items || []).filter(line => numericId(line.variant_id || line.id) === numericId(id));

  function currentCodes(cart) {
    return Array.from(new Set((cart.discount_codes || []).map(value => value.code).filter(Boolean)));
  }

  function otherCodes(cart) {
    return currentCodes(cart).filter(code => !PREFIXES.some(prefix => code.toUpperCase().startsWith(prefix)));
  }

  function giftBenefit(cart, selection) {
    const lines = linesFor(cart, selection.variantNumericId);
    const accepted = (cart.discount_codes || []).find(entry => entry.code && entry.code.toUpperCase() === selection.code.toUpperCase());
    const original = lines.reduce((sum, line) => sum + Number(line.original_line_price || 0), 0);
    const final = lines.reduce((sum, line) => sum + Number(line.final_line_price || 0), 0);
    const unit = Math.max(0, ...lines.map(line => Number(line.original_price || (line.quantity ? line.original_line_price / line.quantity : 0))));
    const allocations = lines.flatMap(line => line.line_level_discount_allocations || []);
    const expectedTitle = 'Gold Luxury Gift — ' + selection.title;
    const ownAllocations = allocations.filter(allocation => {
      const application = allocation.discount_application || {};
      return application.title === expectedTitle || application.title === selection.code || application.code === selection.code;
    });
    const allocated = ownAllocations.reduce((sum, allocation) => sum + Number(allocation.amount || 0), 0);
    const allAllocationsHaveTitles = allocations.length && allocations.every(allocation => allocation.discount_application && allocation.discount_application.title);
    const nativeAmount = ownAllocations.length ? allocated : (allAllocationsHaveTitles ? 0 : original - final);
    return {
      applied: Boolean(accepted && accepted.applicable === true && unit > 0 && nativeAmount >= unit && original - final >= unit),
      original, final, unit, allocated: nativeAmount,
      quantity: lines.reduce((sum, line) => sum + Number(line.quantity || 0), 0)
    };
  }

  function paidProperties(line) {
    const properties = Object.assign({}, line.properties || {});
    [GIFT, CODE, TX].forEach(key => delete properties[key]);
    return properties;
  }

  function createCartLock() {
    let queue = Promise.resolve();
    return task => {
      const run = queue.then(task, task);
      queue = run.catch(() => {});
      return run;
    };
  }

  async function removeOneGift(api, line) {
    if (!line || !isGift(line)) return;
    const quantity = Math.max(0, Number(line.quantity || 0) - 1);
    const change = { id: line.key, quantity };
    if (quantity > 0) change.properties = paidProperties(line);
    await api.change(change);
  }

  async function chooseGift(api, selection, transaction) {
    const before = await api.cart();
    const previous = (before.items || []).filter(isGift);
    const same = previous.find(line => numericId(line.variant_id) === selection.variantNumericId);
    if (same && Number(same.quantity) === 1 && giftBenefit(before, selection).applied) {
      return { success: true, cart: before, already: true };
    }
    const restore = previous.filter(line => {
      const old = { variantNumericId: numericId(line.variant_id), code: line.properties[CODE] || '', title: line.product_title || line.title || '' };
      return old.code && giftBenefit(before, old).applied;
    });
    const removed = [];
    try {
      for (const line of previous) {
        await removeOneGift(api, line);
        removed.push(line.key);
      }
      const fresh = await api.cart();
      const items = [];
      if (!(fresh.items || []).some(isTrigger)) {
        items.push({ id: TRIGGER_ID, quantity: 1, properties: { [TRIGGER]: CAMPAIGN, [TX]: transaction } });
      }
      items.push({ id: selection.variantNumericId, quantity: 1, properties: { [GIFT]: CAMPAIGN, [CODE]: selection.code, [TX]: transaction } });
      await api.add({ items });
      const codes = otherCodes(before).concat(selection.code);
      const after = await api.update({ discount: codes.join(',') });
      if (giftBenefit(after, selection).applied) return { success: true, cart: after };
      const error = new Error('discount-conflict');
      error.reason = 'discount-conflict';
      throw error;
    } catch (error) {
      let recovered = false;
      try {
        const current = await api.cart();
        const added = (current.items || []).filter(line => isGift(line) && line.properties[TX] === transaction);
        for (const line of added) await removeOneGift(api, line);
        const addedCream = (current.items || []).filter(line => line.properties && line.properties[TRIGGER] === CAMPAIGN && line.properties[TX] === transaction);
        for (const line of addedCream) {
          const quantity = Math.max(0, Number(line.quantity || 0) - 1);
          const properties = Object.assign({}, line.properties);
          delete properties[TRIGGER];
          delete properties[TX];
          await api.change({ id: line.key, quantity, properties });
        }
        const restoreRemoved = restore.filter(line => removed.includes(line.key));
        if (restoreRemoved.length) {
          await api.add({ items: restoreRemoved.map(line => ({ id: numericId(line.variant_id), quantity: 1, properties: line.properties })) });
        }
        const restoredCodes = restoreRemoved.length ? currentCodes(before) : otherCodes(before);
        await api.update({ discount: restoredCodes.join(',') });
        recovered = true;
      } catch (recoveryError) {
        console.warn('[Chrissie Gold] Cart recovery could not be confirmed.', recoveryError);
      }
      return { success: false, reason: error.reason || 'request-error', recovered, error };
    }
  }

  async function reconcileCart(api, now) {
    let cart = await api.cart();
    const owned = (cart.items || []).filter(isGift);
    if (!owned.length) return { cart, changed: false };
    const hasCream = (cart.items || []).some(isTrigger);
    const ended = (now === undefined ? Date.now() : now) >= ENDS_AT;
    const invalid = owned.filter(line => {
      const selection = { variantNumericId: numericId(line.variant_id), code: line.properties[CODE] || '', title: line.product_title || line.title || '' };
      return ended || !hasCream || !selection.code || !giftBenefit(cart, selection).applied;
    });
    if (!invalid.length) return { cart, changed: false };
    for (const line of owned) await removeOneGift(api, line);
    cart = await api.update({ discount: otherCodes(cart).join(',') });
    return { cart, changed: true };
  }

  const core = { CAMPAIGN, GIFT, CODE, TX, TRIGGER, TRIGGER_ID, isGift, isTrigger, currentCodes, otherCodes, giftBenefit, paidProperties, removeOneGift, chooseGift, reconcileCart, createCartLock };
  if (typeof module === 'object' && module.exports) {
    module.exports = core;
    return;
  }
  if (window.ChrissieGoldGift) return;
  window.ChrissieGoldGift = core;

  const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
  const url = path => root.replace(/\/$/, '') + '/' + path.replace(/^\//, '');
  async function request(path, body) {
    const response = await fetch(url(path), {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: body ? { Accept: 'application/json', 'Content-Type': 'application/json' } : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined
    });
    const data = await response.json();
    if (!response.ok || data.status >= 400) throw new Error(data.description || data.message || 'Cart request failed');
    return data;
  }
  const api = {
    cart: () => request('cart.js'),
    add: body => request('cart/add.js', body),
    change: body => request('cart/change.js', body),
    update: body => request('cart/update.js', body)
  };
  const cartLock = createCartLock();
  async function waitForNativeCart() {
    for (let i = 0; i < 80; i += 1) {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || !drawer.pendingQuantityUpdates) return;
      await new Promise(resolve => window.setTimeout(resolve, 100));
    }
    throw new Error('Cart update is still pending');
  }
  core.ensure = () => cartLock(async () => {
    await waitForNativeCart();
    const result = await reconcileCart(api);
    if (result.changed) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.refreshView === 'function') await drawer.refreshView();
    }
    return result.cart;
  });
  let dataPromise;
  function gifts(source) {
    if (!dataPromise) dataPromise = fetch(source, { credentials: 'same-origin' }).then(response => {
      if (!response.ok) throw new Error('Gift list unavailable');
      return response.json();
    });
    return dataPromise;
  }

  class GoldGift extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.labels = JSON.parse(this.querySelector('[data-gold-labels]').textContent);
      this.select = this.querySelector('select');
      this.button = this.querySelector('[data-gold-add]');
      this.status = this.querySelector('[data-gold-status]');
      this.select.addEventListener('change', () => this.preview());
      this.button.addEventListener('click', () => this.add());
      this.querySelector('[data-gold-cream-only]')?.addEventListener('click', () => this.creamOnly());
      gifts(this.dataset.source).then(list => {
        this.options = list;
        list.slice().sort((a, b) => a.title.localeCompare(b.title, document.documentElement.lang)).forEach(gift => {
          const option = document.createElement('option');
          option.value = gift.variantNumericId;
          option.textContent = gift.title;
          this.select.appendChild(option);
        });
        this.select.disabled = false;
      }).catch(() => this.message(this.labels.read_error, true));
    }

    message(text, error) {
      this.status.textContent = text;
      this.status.dataset.error = error ? 'true' : 'false';
    }

    async preview() {
      const selected = this.options.find(gift => gift.variantNumericId === this.select.value);
      this.selection = null;
      this.button.disabled = true;
      const preview = this.querySelector('[data-gold-preview]');
      preview.hidden = true;
      this.message('', false);
      if (!selected) return;
      const token = selected.variantNumericId;
      try {
        const product = await request('products/' + encodeURIComponent(selected.handle) + '.js');
        if (this.select.value !== token) return;
        const variant = product.variants.find(item => numericId(item.id) === token);
        if (!variant || !variant.available) {
          this.message(this.labels.stock_error, true);
          return;
        }
        this.selection = Object.assign({}, selected, { currentPrice: variant.price });
        const image = preview.querySelector('img');
        image.src = product.featured_image || product.images[0];
        image.alt = product.title;
        preview.querySelector('[data-gold-name]').textContent = product.title;
        preview.querySelector('[data-gold-value]').textContent = this.labels.value + ' ' + new Intl.NumberFormat(document.documentElement.lang || 'it', { style: 'currency', currency: (window.Shopify.currency || {}).active || 'EUR' }).format(variant.price / 100);
        preview.hidden = false;
        this.button.disabled = this.dataset.triggerAvailable !== 'true';
      } catch (error) {
        if (this.select.value === token) this.message(this.labels.read_error, true);
      }
    }

    busy(value) {
      this.querySelectorAll('button,select').forEach(element => { element.disabled = value; });
      if (!value) this.button.disabled = !this.selection || this.dataset.triggerAvailable !== 'true';
    }

    async add() {
      if (!this.selection || this.locked) return;
      this.locked = true;
      this.busy(true);
      this.message(this.labels.adding, false);
      const transaction = window.crypto && crypto.randomUUID ? crypto.randomUUID() : Date.now() + '-' + Math.random().toString(36).slice(2);
      const result = await cartLock(async () => {
        await waitForNativeCart();
        return chooseGift(api, this.selection, transaction);
      }).catch(error => ({ success: false, reason: 'request-error', error }));
      if (result.success) {
        this.message(this.labels.success, false);
        window.location.assign(url('cart'));
        return;
      }
      this.message(result.reason === 'discount-conflict' && result.recovered ? this.labels.conflict : this.labels.add_error, true);
      this.querySelector('[data-gold-cart-link]').hidden = false;
      this.busy(false);
      this.locked = false;
      refreshCartLabels();
    }

    async creamOnly() {
      if (this.locked) return;
      this.locked = true;
      this.busy(true);
      this.message(this.labels.adding, false);
      try {
        await cartLock(async () => {
          await waitForNativeCart();
          await api.add({ items: [{ id: TRIGGER_ID, quantity: 1, properties: { [TRIGGER]: CAMPAIGN } }] });
        });
        window.location.assign(url('cart'));
      } catch (error) {
        this.message(this.labels.add_error, true);
        this.busy(false);
        this.locked = false;
      }
    }
  }
  customElements.define('chrissie-gold-gift', GoldGift);

  let labelTimer;
  let labelsBusy = false;
  async function refreshCartLabels() {
    if (labelsBusy) return;
    labelsBusy = true;
    try {
      const result = await cartLock(async () => {
        await waitForNativeCart();
        return reconcileCart(api);
      });
      const cart = result.cart;
      if (result.changed) {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.refreshView === 'function') await drawer.refreshView();
        else if (document.querySelector('cart-items')) window.location.reload();
      }
      const marked = (cart.items || []).filter(isGift);
      const selected = marked[0];
      const selection = selected ? { variantNumericId: numericId(selected.variant_id), code: selected.properties[CODE] || '', title: selected.product_title || selected.title || '' } : null;
      const benefit = selection ? giftBenefit(cart, selection) : { applied: false };
      document.querySelectorAll('[data-gold-cart-line]').forEach(element => {
        const line = (cart.items || []).find(item => item.key === element.dataset.lineKey);
        const matches = line && selection && numericId(line.variant_id) === selection.variantNumericId && benefit.applied;
        const group = element.closest('cart-drawer,cart-items,cart-drawer-items') || document;
        const first = Array.from(group.querySelectorAll('[data-gold-cart-line]')).find(item => numericId(item.dataset.variant) === (selection && selection.variantNumericId));
        const show = matches && element === first;
        element.hidden = !show;
        if (show && element.textContent !== element.dataset.label) element.textContent = element.dataset.label;
      });
      document.querySelectorAll('[data-gold-cart-notice]').forEach(element => {
        const hasCream = (cart.items || []).some(isTrigger);
        element.dataset.hasGift = selected ? 'true' : 'false';
        element.hidden = !hasCream && !selected;
        const message = element.querySelector('[data-gold-cart-message]');
        const text = benefit.applied ? element.dataset.success : (selected ? element.dataset.notApplied : element.dataset.choose);
        if (message.textContent !== text) message.textContent = text;
      });
    } catch (error) {
      document.querySelectorAll('[data-gold-cart-line]').forEach(element => { element.hidden = true; });
    } finally {
      labelsBusy = false;
    }
  }
  function scheduleLabels() {
    window.clearTimeout(labelTimer);
    labelTimer = window.setTimeout(refreshCartLabels, 250);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scheduleLabels); else scheduleLabels();
  new MutationObserver(records => {
    const changed = records.some(record => {
      const target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
      return target && target.closest('cart-drawer,cart-items,cart-drawer-items') && !target.closest('[data-gold-cart-line],[data-gold-cart-message]');
    });
    if (changed) scheduleLabels();
  }).observe(document.documentElement, { childList: true, subtree: true });

  let checkoutResuming = false;
  document.addEventListener('submit', event => {
    if (checkoutResuming || event.submitter?.name !== 'checkout' || !document.querySelector('[data-gold-cart-notice][data-has-gift="true"]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const originalForm = event.target;
    const formId = originalForm.id;
    cartLock(async () => {
      await waitForNativeCart();
      const result = await reconcileCart(api);
      if (result.changed) {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.refreshView === 'function') await drawer.refreshView();
      }
      const form = (formId && document.getElementById(formId)) || originalForm;
      const button = form.querySelector('[name="checkout"]');
      checkoutResuming = true;
      if (button) form.requestSubmit(button); else form.requestSubmit();
      window.setTimeout(() => { checkoutResuming = false; }, 0);
    }).catch(() => {
      const notice = document.querySelector('[data-gold-cart-notice]');
      if (notice) {
        notice.hidden = false;
        notice.querySelector('[data-gold-cart-message]').textContent = notice.dataset.readError;
      }
    });
  }, true);
})();
